import { AfterViewInit, Component, NgZone, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { ToastService } from '../toast/toast.service';
import { PhoneService } from '../phone.service';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { GenesysCloudService } from '../genesys-cloud.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConsultTransferComponent } from '../consult-transfer/consult-transfer.component';
import { ExternaliFrameEvent } from '../models/externaliFrameEvent';
import { DialpadService } from '../shared/dialpad.service';
import { ConversationUtils } from '../shared/conversation.utils';
import { LeadConversationState, LeadConversationStateStore } from './leadConversation.state';
import { Router } from '@angular/router';

import { AdmissionUIMessengerService } from '../admission-ui/admission-ui-messenger.service'
import { AdmissionUICommonDataService } from '../admission-ui/admission-ui-common-data.service';
import { AppConfigService } from '../app-config.service';
import { AppInsightsService } from '../app-insights.service';
import { DialBroadcastService } from './dial-broadcast.service';
import { CallService } from '../../lib';

declare let ZOHO: any; // Adjust the type 'any' as needed
@Component({
  selector: 'app-dialpad',
  templateUrl: './dialpad.component.html',
  styleUrl: './dialpad.component.scss'
})
export class DialpadComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly dialogRef: MatDialogRef<ConsultTransferComponent> | undefined;
  private readonly dialClickSubject = new Subject<void>();
  private callTopicSubscription: Subscription | undefined;
  public genesysUserId!: string;
  toastService = inject(ToastService);
  phoneNumber: string = '';
  callState: string = 'none'; // false for dial, true for disconnect
  localTouch: boolean = false;
  waitingOnDisposition: boolean = false;
  keypadRows = this.dialpadService.keypadRows;
  callConversationId: string | undefined;
  emailConversationId: string | undefined;
  customerParticipantId: string | undefined
  agentParticipantId: string | undefined
  queueId: string | undefined
  appInsightsProperties: Record<string, any> = {};
  callStatus: any;
  private readonly terminatedCallConversations = new Set<string>();
  private readonly terminatedEmailConversations = new Set<string>();
  leadConversationState!: LeadConversationState;
  isLeadUserInterface: boolean | undefined = undefined;
  isAdmissionsCompliance: boolean | undefined = undefined;
  leadUIWindow: Window | null = null;
  leadUICurrentConversationId: string | null = null;
  public version: string = '';
  private leadWindowCheckInterval: any;
  private keepLeadWindowOpen: boolean = true;
  private leadUIUrl: string = '';
  private isTransferPhone: boolean = false;
  constructor(
    public phoneService: PhoneService,
    private readonly zone: NgZone,
    private readonly genesysCloudService: GenesysCloudService,
    public dialog: MatDialog,
    private readonly dialpadService: DialpadService,
    private readonly conversationUtils: ConversationUtils,
    private readonly leadConversationStateStore: LeadConversationStateStore,
    private readonly router: Router,
    private readonly admissionUIMessengerService: AdmissionUIMessengerService,
    private readonly admissionUICommonDataService: AdmissionUICommonDataService,
    private readonly appInsightsService: AppInsightsService,
    appConfigService: AppConfigService,
    private readonly dialBroadcastService: DialBroadcastService,
    private callService: CallService
  ) {
    const isLeadInterface = localStorage.getItem('isLeadUserInterface');
    this.isAdmissionsCompliance = localStorage.getItem('isAdmissionCompliance') == null ? undefined : localStorage.getItem('isAdmissionCompliance') === 'true';
    this.isLeadUserInterface = isLeadInterface === null ? undefined : isLeadInterface === 'true';
    if (this.isLeadUserInterface) {
      effect(() => {
        this.leadConversationState = this.leadConversationStateStore.getState()();
        this.waitingOnDisposition = this.leadConversationState.WaitingOnDisposition ?? false;
      });
      // LocalTouch should be set to true when isLeadUserInterface is true
      this.localTouch = true;
    }
    this.genesysUserId = localStorage.getItem('genesysUserId') || 'NOT SET';
    this.version = appConfigService.config?.version ?? '';
  }

  ngOnInit(): void {
    this.admissionUICommonDataService.retrieveModalValidHosts().subscribe((validHosts: string[]) => {
      const currentHost = this.admissionUICommonDataService.normalizeHost(document.location.ancestorOrigins[0] || document.location.host);
      if (validHosts.includes(currentHost)) {
        // Only validhost should subscribe to this
        this.dialBroadcastService.messageReceived$.subscribe((message) => {
          // OutboundCallReceived:  Is trigger via SignalR by DialpadService when user click on dial
          //                        from different application than Genesys where leadUI is not opened, example: LSiDash
          if (message?.type === 'OutboundCallReceived' && this.leadUIWindow) {
            this.admissionUIMessengerService.setOutboundCallDataCrossDomain(message.data.phoneNumber, this.leadUIWindow, "*");
          }

        });
      } else {
        // Only not validhost should subscribe to this
        this.dialBroadcastService.messageReceived$.subscribe((message) => {
          // SetDialpadMessageNotificationReceived:  Is trigger via SignalR by LeadUIService when error from placeCall is received
          // The DialPad from genesys already got the message via postMessage from the LeadUiWindow, other dialpads will get it form this subscription
          if (message?.type === 'SetDialpadMessageNotificationReceived' && !this.leadUIWindow) {
            this.setDialPadNotification(message.data.body);
          }
        });
      }
    });
    this.dialBroadcastService.messageReceived$.subscribe((message) => {
      if (message?.type === 'ToggleLocalTouchReceived') {
        this.localTouch = message.data.localTouch;
      }
    });
    // Set the call state to avoid multiple calls
    this.dialBroadcastService.messageReceived$.subscribe((message) => {
      if (message?.type === 'ChangeCallStateReceived') {
        // Update the call state only if it is different
        if (this.callState !== message.data.callState) {
          this.callState = message.data.callState;
        }
      }
    });

    window.addEventListener('unload', this.handleUnload.bind(this));
    this.clearState();

    this.dialClickSubject.pipe(
      debounceTime(1000) // Adjust time as needed
    ).subscribe(() => {
      this.handleManualDial();
    });
    this.zohoInit();

    let callTopic = 'conversations.calls';
    this.callTopicSubscription = this.genesysCloudService.subscribeToTopic(callTopic).subscribe(notification => {
      this.handleCallEvents(notification.eventBody);
    });

    this.admissionUIMessengerService.monitorSetDialpadInitStateData().subscribe(() => {
      this.clearState();
    });

    this.admissionUIMessengerService.monitorSetDialPadMessageNotification().subscribe((body) => {
      this.setDialPadNotification(body);
    });

    // Add a listener for localStorage changes
    window.addEventListener('storage', this.handleStorageChange.bind(this));

    if (this.isAdmissionsCompliance) {
      this.displayLeadUIWindow();
    }

    // Get conversations for the current user
    this.getConversations();
  }

  private setDialPadNotification(body: string) {
    if (body && this.isAdmissionsCompliance) {
      this.toastService.show({ body: body, classname: 'bg-danger text-light', delay: 5000, header: 'Failure' });
    }
  }

  private getConnectedConversation(conversations: any): any {
    // Select from Entities where participants.calls.state is connected
    return conversations.entities.findLast((entity: any) => {
      return entity.participants.some((participant: any) => {
        const connectedStatuses = this.admissionUICommonDataService.connectedStatus;
        // Just check for connected statuses where the participan is and agent with userId = genesysUserId
        // connected statuses 'connected', 'dialing', 'contacting'
        if (['agent', 'user'].includes(participant.purpose!)
          && participant.user.id === this.genesysUserId
          && participant.held === false
          && connectedStatuses.includes(participant.state)
        ) {
          return participant;
        }
      });
    });
  }

  private getConversations() {
    this.genesysCloudService.getMyCallConversations().subscribe((conversations: any) => {
      let connectedConversation = this.getConnectedConversation(conversations);
      if (connectedConversation) {
        this.callConversationId = connectedConversation.id;
        this.handleCallEvents(connectedConversation);
      }
    });
  }


  private handleStorageChange(event: StorageEvent): void {
    if (event.key === 'isLeadUserInterface') {
      const newValue: boolean | undefined = event.newValue === null ? undefined : event.newValue === 'true';
      this.isLeadUserInterface = newValue;

      if (this.isLeadUserInterface) {
        effect(() => {
          this.leadConversationState = this.leadConversationStateStore.getState()();
          this.waitingOnDisposition = this.leadConversationState.WaitingOnDisposition ?? false;
        });
        this.displayLeadUIWindow();
      }
    }
  }

  private handleUnload(): void {
    this.keepLeadWindowOpen = false; // Don't reopen on main window unload
    if (!this.leadUIWindow) {
      this.appInsightsService.logEvent('dialpadUi- leadUIWindow reference is missing.');
      return;
    }
    this.admissionUIMessengerService.setCanCloseCrossDomain(true, this.leadUIWindow, "*"); // Set canClose to true
  }

  handleCallEvents(conversation: any) {
    if (this.conversationUtils.shouldIgnoreConversation(conversation.id, this.terminatedCallConversations)) {
      return;
    }
    this.appInsightsService.logEvent('dialpadUi-conversation active', {
      conversationId: conversation.id,
    });
    this.conversationUtils.handleParticipantEvents({
      conversation: conversation,
      userId: this.genesysUserId,
      currentCallConversationId: this.callConversationId,
      phoneService: this.phoneService,
      onAgentEvent: (participant) => {
        this.agentParticipantId = participant?.id
        this.callConversationId = conversation.id;
        this.queueId = participant?.queue?.id;
      },
      onCustomerEvent: (phoneNumber, participantId, participant) => {
        // Check event participan state
        if (!this.isDisconnectedOrTerminated(participant))
        {
          this.phoneNumber = phoneNumber;
          this.customerParticipantId = participantId;
        }
      },
      onAlerting: () => {
        this.callState = 'alerting';
      },
      onConnected: this.setConnectedState.bind(this),
      onDisconnected: () => {
        this.conversationUtils.trackTerminatedConversation(conversation.id, this.terminatedCallConversations);
        this.clearState();
      }
    });
    if (this.isLeadUserInterface) this.handleAdmissionsEvents(conversation);
  }

  private isDisconnectedOrTerminated(participant: any): boolean {
    return participant.state === 'disconnected' || participant.state === 'terminated';
  }

  handleAdmissionsEvents(conversation: any) {
    const agentParticipant = this.conversationUtils.getAgentParticipant(conversation, this.genesysUserId);
    if (agentParticipant?.direction === undefined) return;
    if (agentParticipant.attributes?.["leadFlow_Associated_EmailConversationId"] &&
      this.emailConversationId === agentParticipant.attributes["leadFlow_Associated_EmailConversationId"]) {
      return;
    }
    if (agentParticipant.direction !== 'outbound') {
      this.appInsightsService.logEvent('dialpadUi-Admissions events: inbound call', {
        leadConversationId: this.leadUICurrentConversationId,
      });
      if(agentParticipant.state === 'alerting')  {
        if (this.leadUICurrentConversationId !== conversation.id) {
          this.leadUICurrentConversationId = conversation.id;
          this.admissionUICommonDataService.toggleUserStatus('Available').subscribe();
          this.appInsightsService.logEvent('dialpadUi-Admissions events: inbound call different Id', {
            leadConversationId: this.leadUICurrentConversationId,
          });
          this.admissionUIMessengerService.setInboundCallDataCrossDomain(this.phoneNumber, conversation.id, this.leadUIWindow!, "*");
        }
      }
    }
  }

  setConnectedState() {
    this.callState = 'connected';
  }

  private clearState() {
    this.conversationUtils.clearConversationState(
      this.callConversationId,
      this.emailConversationId,
      this.terminatedCallConversations,
      this.terminatedEmailConversations
    );

    this.customerParticipantId = undefined;
    this.agentParticipantId = undefined;
    this.callConversationId = undefined;
    this.emailConversationId = undefined;
    this.callState = 'none';
    this.phoneNumber = '';
    this.queueId = undefined;
    if (this.dialogRef) this.dialogRef.close();
  }

  ngAfterViewInit(): void {
    window.addEventListener("message", this.handleClickToDial.bind(this), false);
  }

  ngOnDestroy(): void {
    this.dialClickSubject.complete();
    this.callTopicSubscription?.unsubscribe();
    window.removeEventListener("message", this.handleClickToDial.bind(this), false);

    // Clear the lead window check interval
    if (this.leadWindowCheckInterval) {
      clearInterval(this.leadWindowCheckInterval);
    }
    this.keepLeadWindowOpen = false;
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
  }

  handleClickToDial(event: Event) {
    let message = event as MessageEvent;
    let clickToDialData = message.data as ExternaliFrameEvent;
    if (clickToDialData?.eventType?.toLocaleLowerCase() !== "dial" && clickToDialData?.eventType?.toLocaleLowerCase() !== "transfer") { return; }

    // Change the CallState to loading to prevent multiple simultaneous calls
    // when click from other apps like zoho or LSiDash
    if (this.callState === 'none') {
      this.callState = 'loading';
      // Send the loading state to other dialpads, no avoid multiple calls
      this.admissionUIMessengerService.setCallState(this.callState);
    }

    if(clickToDialData?.eventType?.toLocaleLowerCase() === "transfer"){ 
      this.isTransferPhone = true;
    }

    this.phoneNumber = this.phoneService.formatPhoneNumber(clickToDialData.phoneNumber);
    this.localTouch = clickToDialData.isLocalTouch;
    // Update the localTouch state in multiple Dialpads. Example: LSiDash and Genesys
    this.admissionUIMessengerService.sendLocalTouchToValue(this.localTouch);
    this.dialClickSubject.next();
  }

  zohoInit() {
    const component = this;// Only initialize if running inside an iframe (embedded context)
    let isZohoParent = this.isZohoParent();
    if (isZohoParent && typeof ZOHO !== 'undefined' && ZOHO.embeddedApp) {
      component.zone.run(() => {
        ZOHO.embeddedApp.on("Dial", function (data: any) {
          component.clearState();
          for (let digit of data.Number) {
            component.appendToPhoneNumber(digit);
          }
          component.zone.run(() => {
            ZOHO.CRM.UI.Dialer.maximize()
          });
          component.handleDial()
        });
        ZOHO.embeddedApp.init();
      });
    }
  }

  isZohoParent(): boolean {
    const currentHost = this.admissionUICommonDataService.normalizeHost(document.location?.ancestorOrigins?.[0] || document.location?.host);
    return window.parent !== window && currentHost?.toLowerCase().includes('zoho');
  }

  appendToPhoneNumber(digit: number): void {
    this.phoneNumber = this.phoneService.appendToPhoneNumber(this.phoneNumber, digit);
  }

  handleManualDial() {
    if (this.isAdmissionsCompliance) {
      this.handleManualDialAdmissions();
    } else {
      this.toggleCall();
    }
  }

  handleManualDialAdmissions() {
    this.conversationUtils.handleManualDial({
      phoneNumber: this.phoneNumber,
      phoneService: this.phoneService,
      onValidNumber: () => {
        this.phoneService.isValidStudent(this.phoneNumber).then(_isStudent => {
          if (this.callState === 'none') {
            this.callState = 'loading';
            // Send the loading state to other dialpads, no avoid multiple calls
            this.admissionUIMessengerService.setCallState(this.callState);
          }
          const isCallConnected = ['connected', 'alerting', 'dialing', 'contacting', 'offering'].includes(this.callState);
          if (_isStudent && !isCallConnected) {
            this.admissionUIMessengerService.setOutboundCallDataCrossDomain(this.phoneNumber, this.leadUIWindow!, "*");
          } else if (!_isStudent && !isCallConnected) {
            this.handleOutboundCallWhenNoStudentMapped();
          }
          else {
            if(isCallConnected && this.isTransferPhone){
              this.callService.apiCallConsultPost({
              address: this.phoneNumber,
              conversationId: this.callConversationId ?? '',
              customerParticipantId: this.customerParticipantId
              }).subscribe((response) => {
                console.log('Consult transfer successful', response);
              });
            }else{
              this.toggleCall()
            }
          };
        }).catch((err) => { this.callState = 'none' });
      }
    });
  }

  toggleCall(): void {
    this.conversationUtils.toggleCall({
      conversationId: this.callConversationId,
      queueId: this.queueId,
      participantId: this.agentParticipantId,
      phoneNumber: this.phoneNumber,
      callState: this.callState,
      phoneService: this.phoneService,
      localTouch: this.localTouch,
      updateCallState: (newState) => this.callState = newState,
      updateConversationId: (conversationId) => this.callConversationId = conversationId,
      clearState: this.clearState.bind(this),
      showError: (errorMessage) => this.toastService.show({
        body: errorMessage,
        classname: 'bg-danger text-light',
        delay: 5000,
        header: 'Disconnect Failed'
      })
    });
  }

  validateInput(event: KeyboardEvent): boolean {
    return this.phoneService.validateInput(event, this.phoneNumber)
  }

  isValidNumber() {
    return this.phoneService.isValidNumber(this.phoneNumber)
  }

  togglelocalTouch() {
    this.localTouch = !this.localTouch;
    this.admissionUIMessengerService.sendLocalTouchToValue(this.localTouch);
  }

  handleDial() {
    if (this.callState === 'none') {
      this.callState = 'loading';
      // Send the loading state to other dialpads, no avoid multiple calls
      this.admissionUIMessengerService.setCallState(this.callState);
    }
    this.dialClickSubject.next();
  }

  onConsultTransfer() {
    this.conversationUtils.handleConsultTransfer({
      dialog: this.dialog,
      callConversationId: this.callConversationId,
      customerParticipantId: this.customerParticipantId,
      agentParticipantId: this.agentParticipantId,
      onDialogClosed: (result) => {
      }
    });
  }

  onPaste(event: ClipboardEvent): void {
    this.phoneService.onPaste(event);
  }

  displayLeadUIWindow() {
    this.admissionUICommonDataService.retrieveModalValidHosts().subscribe(validHosts => {
      const currentHost = this.admissionUICommonDataService.normalizeHost(document.location.ancestorOrigins[0] || document.location.host);
      if (validHosts.includes(currentHost)) {
        const route = '/leadui/idle';
        const url = this.router.serializeUrl(this.router.createUrlTree([route]));
        this.leadUIUrl = url;
        this.openLeadUIWindow();

        // Set up interval to check if window is closed and reopen if needed
        this.keepLeadWindowOpen = true;
        this.leadWindowCheckInterval = setInterval(() => {
          this.checkAndReopenLeadWindow()
        }, 2000);
      }
    });
  }

  private openLeadUIWindow() {
    const popupName = 'leadUIWindow'; // Use a consistent name for the popup
    const popupFeatures = 'popup, width=800, height=650';
    this.leadUIWindow = window.open(this.leadUIUrl, popupName, popupFeatures);
    if (this.leadUIWindow) {
      this.admissionUIMessengerService.sendLocalTouchToValue(this.localTouch);
    }
  }

  private checkAndReopenLeadWindow() {
    if (this.keepLeadWindowOpen && (!this.leadUIWindow || this.leadUIWindow.closed)) {
      this.openLeadUIWindow();
    }
  }

  private handleOutboundCallWhenNoStudentMapped(): void {
    if (this.callState === 'none' || this.callState === 'loading') {
      this.callState = 'dialing';
      this.phoneService.placeAdmissionsOutboundCallByPhoneNumber(this.phoneNumber).then(conversationId => {
        if (!conversationId) {
          this.callState = 'none';
        } else {
          this.callConversationId = conversationId;
        }
      });
    }
    else {
      if (this.callConversationId) {
        this.phoneService.disconnect(this.callConversationId).then(() => {
          this.clearState();
        });
      }
    }
  }
}

